import type { ActionFunctionArgs } from '~/types/route-args';
import { data as routerData } from 'react-router';
import { z } from 'zod';
import dayjs from 'dayjs';
import type {
    CreateEnquiryMutation,
    CreateEnquiryMutationVariables,
    CreateMeetingRequestRecordMutation,
    CreateMeetingRequestRecordMutationVariables,
    GetMeetingRequestContextQuery,
    GetMeetingRequestContextQueryVariables,
} from 'generated/graphql';
import { EnquiryChannel } from 'generated/graphql';
import { CreateEnquiryDocument } from 'generated/documents/CreateEnquiryDocument';
import { CreateMeetingRequestRecordDocument } from 'generated/documents/CreateMeetingRequestRecordDocument';
import { GetMeetingRequestContextDocument } from 'generated/documents/GetMeetingRequestContextDocument';

import type { ProfessionalEnquiryContext } from '~/modules/enquiry/util/get-default-enquiry-message';
import { getMeetingRequestMessage } from '~/modules/enquiry/util/get-default-enquiry-message';

export const BOOKING_REQUEST_API_ROUTE = '/remix-api/booking/request';

/**
 * Mirrors {@link BookingActionData} so the booking hook can drive this route
 * with no branching: it only reads `ok` and `message`.
 */
export interface BookingRequestActionData {
    ok: boolean;
    message: string;
    /**
     * The request cannot go out as the current session: there is none, or it
     * names a different account than the page the reader submitted from. The
     * gate answers by standing the auth wall back up and re-sending once the
     * reader has signed in.
     */
    needsLogin?: boolean;
}

/**
 * Whether a string names a timezone the runtime can actually resolve.
 *
 * `Intl` is the same resolver dayjs's timezone plugin uses underneath, so a
 * zone that passes here cannot throw later while the enquiry body is composed.
 */
const isRealTimezone = (zone: string): boolean => {
    try {
        new Intl.DateTimeFormat(undefined, { timeZone: zone });
        return true;
    } catch {
        return false;
    }
};

const FORM_SCHEMA = z.object({
    professionalUrlSegment: z.string().min(1),
    siteTreeId: z.coerce.number().int().positive(),
    // Interpolated into the enquiry body, so it is bounded like every other
    // free-text field here rather than trusted at whatever length it arrives.
    itemTitle: z.string().trim().max(200).optional(),
    startTime: z.string().min(1),
    duration: z.coerce.number().int().positive().max(480),
    // An unknown zone would throw out of `dayjs().tz()` while the body is
    // composed, escaping as an HTML error page rather than the JSON shape the
    // booking hook expects. Checked for being a real IANA zone rather than
    // against the card's own list: the picker deliberately carries a visitor's
    // browser zone when it is not one of the twelve it offers, so that list
    // would reject legitimate requests from most of the world.
    timezoneName: z.string().refine(isRealTimezone, {
        message: 'Unknown timezone.',
    }),
    // The TeamMember row, not the member account. A display-only person has no
    // account to send, and a row id is already on the page that offered them.
    teamMemberId: z.coerce.number().int().positive().optional(),
    organizerMemberId: z.coerce.number().int().positive().optional(),
    clientCompany: z.string().trim().max(100).optional(),
    // The identity the page was displaying when the reader pressed send. The
    // session decides who the enquiry belongs to, so this is an assertion to
    // verify, never a value to attribute from: when the cookies have moved to
    // another account behind the page (a login in another tab, a different
    // checkout on the same machine), the two disagree and the request must not
    // go out as someone the reader never saw. Empty when the page could not
    // name anyone — a send straight off the auth wall runs before the loader
    // data behind the page has caught up — and an empty assertion asserts
    // nothing: the wall just authenticated whoever the session now holds.
    clientEmail: z.string().trim().max(254).optional(),
});

const failure = (message: string, status: number, needsLogin = false) =>
    routerData<BookingRequestActionData>(
        { ok: false, message, needsLogin },
        { status },
    );

/**
 * Turns a preferred meeting time into an enquiry, for professionals with no
 * connected calendar.
 *
 * Nothing here reaches a calendar provider: the visitor is asking, and the
 * professional confirms from their inbox. The message is composed here rather
 * than posted, so it always matches the slot that was chosen. See BC-5174.
 */
export const action = async ({ context, request }: ActionFunctionArgs) => {
    const { client, clearUserCache } = context;

    // Booking is behind the auth gate, so an unauthenticated post is either a
    // stale tab or something bypassing the UI. Either way there is no member to
    // attribute the request to, and nothing here should create one.
    const member = context.user;
    if (member?.__typename !== 'Me') {
        return failure('Please log in to send your request.', 401, true);
    }

    const clientEmail = member.Email?.trim();
    if (!clientEmail) {
        return failure('Your account has no email address.', 400);
    }
    const clientName =
        [member.FirstName, member.LastName].filter(Boolean).join(' ').trim() ||
        clientEmail;

    const form = await request.formData();

    const parsed = FORM_SCHEMA.safeParse(
        Object.fromEntries(
            [...form.entries()].filter(
                ([, value]) => typeof value === 'string',
            ),
        ),
    );

    if (!parsed.success) {
        return failure('Please check your details and try again.', 400);
    }

    // The same check the standard enquiry flow gets from the createEnquiry
    // mutation (posted email must equal the session member's), made here
    // because this route composes the mutation's Email from the session — so
    // the mutation's own comparison can never disagree with itself. Without
    // this, a page drawn for one account while the cookies hold another sends
    // silently as the cookie account, filed into that account's conversation.
    const assertedEmail = parsed.data.clientEmail;
    if (
        assertedEmail &&
        assertedEmail.toLowerCase() !== clientEmail.toLowerCase()
    ) {
        return failure(
            'You are signed in as a different account. Please log in again to send your request.',
            409,
            true,
        );
    }

    const {
        professionalUrlSegment,
        siteTreeId,
        itemTitle,
        startTime,
        duration,
        timezoneName,
        teamMemberId,
        organizerMemberId,
        clientCompany,
    } = parsed.data;

    const start = dayjs(startTime);
    if (!start.isValid()) {
        return failure('Please choose a time and try again.', 400);
    }

    // Resolve the chosen person's name from the professional's own roster
    // rather than trusting a posted name.
    let personName: string | null = null;
    // What they sell, which decides what a profile-raised meeting is about.
    let professionalContext: ProfessionalEnquiryContext | null = null;
    try {
        const { data } = await client.query<
            GetMeetingRequestContextQuery,
            GetMeetingRequestContextQueryVariables
        >({
            query: GetMeetingRequestContextDocument,
            variables: { URLSegment: professionalUrlSegment },
        });

        const professional = data?.getProfessionalByURLSegment;
        if (!professional?.AllowsEnquiries) {
            return failure('This professional is not taking enquiries.', 403);
        }

        professionalContext = {
            IsManufacturer: professional.IsManufacturer ?? undefined,
            HasProducts: professional.HasProducts ?? undefined,
            ProductsAllowed: professional.ProductsAllowed ?? undefined,
        };

        // Matching on the roster is the check as well as the lookup: a row that
        // is not among this professional's public team members yields no name,
        // so the request goes out addressed to the practice rather than to
        // somebody the reader was never shown.
        if (teamMemberId) {
            personName =
                professional.PaginatedTeamMembers.edges.find(
                    (edge) => edge.node.ID === teamMemberId,
                )?.node.Name ?? null;
        } else if (organizerMemberId) {
            personName =
                professional.PaginatedTeamMembers.edges.find(
                    (edge) => edge.node.MemberID === organizerMemberId,
                )?.node.Name ?? null;
        }
    } catch {
        return failure('We could not send your request just now.', 500);
    }

    const message = getMeetingRequestMessage({
        personName,
        itemTitle,
        professional: professionalContext,
        companyName: clientCompany,
        startTimestamp: start.valueOf(),
        timezoneName,
        durationMinutes: duration,
    });

    try {
        const { data } = await client.mutate<
            CreateEnquiryMutation,
            CreateEnquiryMutationVariables
        >({
            mutation: CreateEnquiryDocument,
            variables: {
                EnquiryInput: {
                    Name: clientName,
                    Email: clientEmail,
                    Message: message,
                    CreatedAt: Date.now(),
                    SiteTreeIDs: [siteTreeId],
                    // CreateEnquiry rejects ShareContactDetails without a phone
                    // ("When sharing contact details a phone is required"), and
                    // this flow collects no phone.
                    ShareContactDetails: false,
                    // The gate guarantees a member before this runs, so there is
                    // never an account to create here.
                    AutoCreateMember: false,
                    TargetProfessionals: [],
                    // A name resolved off the roster means the request is
                    // addressed to that person; otherwise it goes to the
                    // practice. Conversion reporting counts either channel as a
                    // meeting, never as a plain enquiry.
                    Channel: personName
                        ? EnquiryChannel.BookMeetingMember
                        : EnquiryChannel.BookMeetingCompany,
                },
            },
        });

        await clearUserCache();

        const result = data?.createEnquiry;

        if (result?.__typename !== 'CreateEnquiryResult') {
            return failure('We could not send your request just now.', 500);
        }

        // Attach the meeting record that gives the request a manageable
        // lifecycle (confirm / decline / cancel). The enquiry is already sent
        // and carries the conversation, so a failure here degrades to the
        // pre-lifecycle behaviour rather than failing the request.
        const enquiryId = result.Enquiries?.[0]?.ID;
        if (enquiryId) {
            try {
                await client.mutate<
                    CreateMeetingRequestRecordMutation,
                    CreateMeetingRequestRecordMutationVariables
                >({
                    mutation: CreateMeetingRequestRecordDocument,
                    variables: {
                        input: {
                            EnquiryID: enquiryId,
                            StartTime: start.toISOString(),
                            DurationMinutes: duration,
                            TimezoneName: timezoneName,
                            TeamMemberID: teamMemberId,
                        },
                    },
                });
            } catch {
                // Logged server-side; the request itself succeeded.
            }
        }

        return routerData<BookingRequestActionData>({
            ok: true,
            message: 'Request sent',
        });
    } catch {
        return failure('We could not send your request just now.', 500);
    }
};
